// frontend/src/pages/Subcategories.tsx

import React, { useContext, useEffect, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  getSubcategories,
  createSubcategory,
  updateSubcategory,
  deleteSubcategory,
} from "../api/subcategories";
import {
  Subcategory,
  CreateSubcategoryRequest,
  UpdateSubcategoryRequest,
} from "../types/subcategory";
import Sidebar from "../components/Sidebar";
import SubcategoryForm from "../components/SubcategoryForm";
import EditSubcategoryForm from "../components/EditSubcategoryForm";
import {
  Plus,
  Download,
  Search,
  Filter,
  Edit3,
  Trash2,
  Tags,
} from "lucide-react";
import { generatePaginationItems } from "../utils/pagination";

export default function Subcategories() {
  const { user } = useContext(AuthContext);

  // — Data state —
  const [list, setList] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(true);

  // — UI state for forms —
  const [showNew, setShowNew] = useState(false);
  const [editTarget, setEditTarget] = useState<Subcategory | null>(null);

  // — Filter state —
  // now includes the new 'vat-refundable' type
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<
    "all" | Subcategory["categoryType"]
  >("all");

  // — Pagination state —
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // 1) Fetch subcategories once
  useEffect(() => {
    let mounted = true;
    (async () => {
      const data = await getSubcategories();
      if (!mounted) return;
      setList(data);
      setLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // 2) Build unique type options for the filter dropdown
  const typeOptions = useMemo(() => {
    return Array.from(new Set(list.map((s) => s.categoryType))).sort();
  }, [list]);

  // 3) Apply search + type filters
  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();

    return list.filter((s) => {
      const matchesSearch = s.name.toLowerCase().includes(q);
      const matchesType =
        filterType === "all" || s.categoryType === filterType;

      return matchesSearch && matchesType;
    });
  }, [list, searchTerm, filterType]);

  // 4) Pagination calculations
  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / rowsPerPage),
  );

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, currentPage, rowsPerPage]);

  // 5) Reset page if filtering shrinks pages
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // 6) Authorization guards
  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!["admin", "superadmin"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // — CRUD handlers —
  const handleCreate = async (d: CreateSubcategoryRequest) => {
    const s = await createSubcategory(d);
    setList((prev) => [s, ...prev]);
    setShowNew(false);
  };

  const handleUpdate = async (
    id: string,
    d: UpdateSubcategoryRequest,
  ) => {
    const s = await updateSubcategory(id, d);
    setList((prev) => prev.map((x) => (x.id === id ? s : x)));
    setEditTarget(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this subcategory?")) return;

    await deleteSubcategory(id);
    setList((prev) => prev.filter((x) => x.id !== id));
  };

  // — CSV export logic —
  const csvEscape = (v: string | number) => {
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };

  const handleExport = () => {
    const headers = ["Name", "Type", "CreatedBy"];

    const rows = list.map((s) => [
      s.name,
      s.categoryType,
      s.createdBy?.username || s.createdByName || "",
    ]);

    const csv = [
      headers.map(csvEscape).join(","),
      ...rows.map((r) => r.map(csvEscape).join(",")),
    ].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = "subcategories_export.csv";
    a.click();

    URL.revokeObjectURL(url);
  };

  const fieldClass = `
    box-border h-10 w-full min-w-0 rounded-[9px]
    border border-slate-200 bg-white px-3
    text-[12px] font-medium text-slate-800
    outline-none transition
    placeholder:text-slate-400
    hover:border-slate-300
    focus:border-emerald-400
    focus:ring-2 focus:ring-emerald-100
  `;

  const startIndex = (currentPage - 1) * rowsPerPage;

  return (
    <div className="min-h-screen bg-[#f7f8fb]">
      <Sidebar />

      <main
        className="
          ml-[280px] min-w-0 px-6 py-6
          transition-[margin] duration-200
          [.sidebar-collapsed_&]:ml-[76px]
          max-lg:ml-[76px]
          max-lg:px-4
          max-md:ml-0
          max-md:px-4
          max-sm:px-3
          max-sm:py-4
        "
      >
        <div className="mx-auto w-full max-w-[1600px]">
          {/* Header */}
          <header
            className="
              mb-5 flex items-start justify-between gap-4
              max-md:flex-col max-md:items-stretch
            "
          >
            <div className="flex min-w-0 items-start gap-3">
              <div
                className="
                  grid h-10 w-10 shrink-0 place-items-center
                  rounded-[10px] bg-slate-100 text-slate-600
                "
              >
                <Tags size={20} strokeWidth={1.8} />
              </div>

              <div className="min-w-0">
                <div
                  className="
                    mb-1 text-[10px] font-bold uppercase
                    tracking-[0.12em] text-blue-600
                  "
                >
                  Expense management
                </div>

                <h1
                  className="
                    m-0 text-[28px] font-bold
                    tracking-[-0.03em] text-slate-950
                    max-sm:text-[24px]
                  "
                >
                  Subcategories
                </h1>

                <p className="mb-0 mt-1 text-[12px] leading-5 text-slate-500">
                  Manage expense subcategories and tax classifications.
                </p>
              </div>
            </div>

            <div
              className="
                flex shrink-0 items-center gap-2
                max-sm:grid max-sm:grid-cols-2
              "
            >
              <button
                type="button"
                onClick={handleExport}
                className="
                  inline-flex h-10 items-center justify-center gap-2
                  rounded-[9px]
                  border border-emerald-200
                  bg-white px-4
                  text-[11px] font-semibold text-emerald-700
                  transition
                  hover:border-emerald-300
                  hover:bg-emerald-50
                  hover:text-emerald-800
                  focus:outline-none
                  focus:ring-2
                  focus:ring-emerald-200
                  max-sm:w-full
                "
              >
                <Download size={15} />
                Export
              </button>

              <button
                type="button"
                onClick={() => setShowNew(true)}
                className="
                  inline-flex h-10 items-center justify-center gap-2
                  rounded-[9px]
                  border border-emerald-600
                  bg-emerald-600 px-4
                  text-[11px] font-semibold text-white
                  transition
                  hover:border-emerald-700
                  hover:bg-emerald-700
                  focus:outline-none
                  focus:ring-2
                  focus:ring-emerald-200
                  max-sm:w-full
                "
              >
                <Plus size={15} />
                New Subcategory
              </button>
            </div>
          </header>

          {/* Search / Filter */}
          <section
            className="
              mb-4 rounded-[16px]
              border border-slate-200 bg-white
              p-4
              shadow-[0_1px_3px_rgba(15,23,42,0.04)]
              max-sm:p-3
            "
          >
            <div
              className="
                grid grid-cols-1 gap-3
                md:grid-cols-[minmax(0,1fr)_220px]
              "
            >
              <label className="min-w-0">
                <span
                  className="
                    mb-1.5 block
                    text-[10px] font-semibold uppercase
                    tracking-[0.04em] text-slate-400
                  "
                >
                  Search
                </span>

                <div className="relative">
                  <Search
                    size={16}
                    className="
                      pointer-events-none absolute left-3 top-1/2
                      -translate-y-1/2 text-slate-400
                    "
                  />

                  <input
                    type="text"
                    placeholder="Search by name…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`${fieldClass} pl-9`}
                  />
                </div>
              </label>

              <label className="min-w-0">
                <span
                  className="
                    mb-1.5 block
                    text-[10px] font-semibold uppercase
                    tracking-[0.04em] text-slate-400
                  "
                >
                  Type
                </span>

                <div className="relative">
                  <Filter
                    size={15}
                    className="
                      pointer-events-none absolute left-3 top-1/2
                      -translate-y-1/2 text-slate-400
                    "
                  />

                  <select
                    value={filterType}
                    onChange={(e) =>
                      setFilterType(e.target.value as any)
                    }
                    className={`${fieldClass} pl-9 pr-8`}
                  >
                    <option value="all">All Types</option>

                    {typeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t === "vat-refundable"
                          ? "VAT-Refundable"
                          : t.charAt(0).toUpperCase() + t.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
            </div>
          </section>

          {/* Table or Loading */}
          {loading ? (
            <section
              className="
                flex min-h-[260px] flex-col items-center justify-center
                rounded-[16px]
                border border-slate-200 bg-white
                shadow-[0_1px_3px_rgba(15,23,42,0.04)]
              "
            >
              <span
                className="
                  h-6 w-6 animate-spin rounded-full
                  border-[3px] border-slate-200 border-t-slate-700
                "
              />

              <span className="mt-3 text-[11px] font-medium text-slate-500">
                Loading subcategories…
              </span>
            </section>
          ) : (
            <section
              className="
                overflow-hidden rounded-[16px]
                border border-slate-200 bg-white
                shadow-[0_1px_3px_rgba(15,23,42,0.04)]
              "
            >
              <div
                className="
                  flex items-center justify-between gap-4
                  border-b border-slate-100
                  px-5 py-4
                  max-sm:flex-col max-sm:items-start max-sm:px-4
                "
              >
                <div>
                  <h2 className="m-0 text-[18px] font-bold text-slate-950">
                    Subcategories
                  </h2>

                  <p className="mb-0 mt-1 text-[13px] text-slate-500">
                    Showing{" "}
                    <strong className="font-semibold text-slate-600">
                      {filtered.length}
                    </strong>{" "}
                    of{" "}
                    <strong className="font-semibold text-slate-600">
                      {list.length}
                    </strong>{" "}
                    subcategor{list.length === 1 ? "y" : "ies"}
                  </p>
                </div>

                <div className="rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-500">
                  <strong className="font-semibold text-slate-800">
                    {paginated.length}
                  </strong>{" "}
                  on this page
                </div>
              </div>

              {filtered.length === 0 ? (
                <div
                  className="
                    flex min-h-[230px] flex-col
                    items-center justify-center px-6 text-center
                  "
                >
                  <div
                    className="
                      grid h-11 w-11 place-items-center
                      rounded-xl bg-slate-100 text-slate-400
                    "
                  >
                    <Tags size={20} />
                  </div>

                  <h3 className="mb-0 mt-3 text-[13px] font-semibold text-slate-900">
                    No subcategories found
                  </h3>

                  <p
                    className="
                      mb-0 mt-1 max-w-[430px]
                      text-[10px] leading-5 text-slate-500
                    "
                  >
                    {searchTerm
                      ? "No subcategories found matching your search."
                      : "No subcategories to display."}
                  </p>
                </div>
              ) : (
                <>
                  <div
                    className="
                      w-full overflow-x-auto
                      [-webkit-overflow-scrolling:touch]
                    "
                  >
                    <table className="w-full min-w-[720px] border-collapse">
                      <thead className="bg-slate-50/80">
                        <tr>
                          {["Name", "Type", "Created By"].map((label) => (
                            <th
                              key={label}
                              className="
                                px-4 py-3.5 text-left
                                text-[12px] font-semibold uppercase
                                tracking-[0.04em] text-slate-600
                              "
                            >
                              {label}
                            </th>
                          ))}

                          <th
                            className="
                              w-[110px] px-4 py-3.5 text-center
                              text-[12px] font-semibold uppercase
                              tracking-[0.04em] text-slate-600
                            "
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {paginated.map((s) => (
                          <tr
                            key={s.id}
                            className="
                              border-b border-slate-100
                              transition-colors
                              last:border-b-0
                              hover:bg-slate-50/60
                            "
                          >
                            <td className="px-4 py-4 align-middle">
                              <div className="min-w-[180px] text-[13px] font-semibold text-slate-900">
                                {s.name}
                              </div>
                            </td>

                            <td className="px-4 py-4 align-middle">
                              <span
                                className={`
                                  inline-flex rounded-full px-2.5 py-1
                                  text-[11px] font-semibold
                                  ${
                                    s.categoryType === "non-taxable"
                                      ? "bg-slate-100 text-slate-700"
                                      : s.categoryType === "vat-refundable"
                                        ? "bg-blue-50 text-blue-700"
                                        : "bg-amber-50 text-amber-700"
                                  }
                                `}
                              >
                                {s.categoryType === "vat-refundable"
                                  ? "VAT-Refundable"
                                  : s.categoryType}
                              </span>
                            </td>

                            <td className="px-4 py-4 align-middle">
                              <div className="min-w-[150px] text-[13px] text-slate-600">
                                {s.createdBy?.username || s.createdByName}
                              </div>
                            </td>

                            <td className="px-4 py-4 text-center align-middle">
                              <div className="inline-flex items-center justify-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => setEditTarget(s)}
                                  title="Edit"
                                  className="
                                    inline-flex h-8 w-8 items-center justify-center
                                    rounded-lg
                                    border border-emerald-200
                                    bg-white p-0 text-emerald-700
                                    transition
                                    hover:border-emerald-300
                                    hover:bg-emerald-50
                                    hover:text-emerald-800
                                    focus:outline-none
                                    focus:ring-2
                                    focus:ring-emerald-200
                                  "
                                >
                                  <Edit3 size={15} />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleDelete(s.id)}
                                  title="Delete"
                                  className="
                                    inline-flex h-8 w-8 items-center justify-center
                                    rounded-lg
                                    border border-red-200
                                    bg-white p-0 text-red-600
                                    transition
                                    hover:border-red-300
                                    hover:bg-red-50
                                    hover:text-red-700
                                    focus:outline-none
                                    focus:ring-2
                                    focus:ring-red-200
                                  "
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  {filtered.length > rowsPerPage && (
                    <div
                      className="
                        flex items-center justify-between gap-4
                        border-t border-slate-100
                        px-5 py-3
                        max-sm:flex-col max-sm:items-stretch max-sm:px-4
                      "
                    >
                      <div
                        className="
                          flex flex-wrap items-center gap-2
                          text-[13px] text-slate-600
                        "
                      >
                        <span>Rows per page</span>

                        <select
                          value={rowsPerPage}
                          onChange={(e) => {
                            setRowsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                          className="
                            h-8 rounded-lg
                            border border-slate-200 bg-white
                            px-2 text-[13px] font-medium text-slate-700
                            outline-none
                            focus:border-emerald-400
                            focus:ring-2
                            focus:ring-emerald-100
                          "
                        >
                          {[5, 10, 20, 50].map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>

                        <span>
                          {startIndex + 1}–
                          {Math.min(
                            startIndex + rowsPerPage,
                            filtered.length,
                          )}{" "}
                          of {filtered.length}
                        </span>
                      </div>

                      <div
                        className="
                          flex flex-wrap items-center justify-end gap-1.5
                          max-sm:justify-start
                        "
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setCurrentPage((p) => Math.max(p - 1, 1))
                          }
                          disabled={currentPage === 1}
                          className="
                            h-8 rounded-lg
                            border border-emerald-200
                            bg-white px-3
                            text-[13px] font-semibold text-emerald-700
                            transition
                            hover:border-emerald-300
                            hover:bg-emerald-50
                            disabled:cursor-not-allowed
                            disabled:opacity-40
                          "
                        >
                          Prev
                        </button>

                        {generatePaginationItems(
                          currentPage,
                          totalPages,
                        ).map((item, index) =>
                          item.type === "ellipsis" ? (
                            <span
                              key={`ellipsis-${index}`}
                              className="
                                inline-flex h-8 min-w-8
                                items-center justify-center
                                text-[13px] font-semibold text-slate-500
                              "
                            >
                              …
                            </span>
                          ) : (
                            <button
                              type="button"
                              key={item.page}
                              onClick={() => setCurrentPage(item.page!)}
                              className={`
                                h-8 min-w-8 rounded-lg border px-2
                                text-[13px] font-semibold transition
                                ${
                                  item.isActive
                                    ? `
                                        border-emerald-600
                                        bg-emerald-600
                                        text-white
                                      `
                                    : `
                                        border-emerald-200
                                        bg-white
                                        text-emerald-700
                                        hover:border-emerald-300
                                        hover:bg-emerald-50
                                      `
                                }
                              `}
                            >
                              {item.page}
                            </button>
                          ),
                        )}

                        <button
                          type="button"
                          onClick={() =>
                            setCurrentPage((p) =>
                              Math.min(p + 1, totalPages),
                            )
                          }
                          disabled={currentPage === totalPages}
                          className="
                            h-8 rounded-lg
                            border border-emerald-200
                            bg-white px-3
                            text-[13px] font-semibold text-emerald-700
                            transition
                            hover:border-emerald-300
                            hover:bg-emerald-50
                            disabled:cursor-not-allowed
                            disabled:opacity-40
                          "
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {/* Modals */}
          {showNew && (
            <SubcategoryForm
              onSubmit={handleCreate}
              onCancel={() => setShowNew(false)}
            />
          )}

          {editTarget && (
            <EditSubcategoryForm
              sub={editTarget}
              onSubmit={handleUpdate}
              onCancel={() => setEditTarget(null)}
            />
          )}
        </div>
      </main>
    </div>
  );
}
